import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { searchRecipes } from '../../api/recipe'
import routes, { draftRecipeLink, groupRecipeLink } from '../../routers'
import { DRAFT_STATUS } from '../../utils/constants'
import { creditLine } from '../../utils/recipeCredit'

import './style.css'

const WAIT_BEFORE_SEARCH_MS = 250

const SearchIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="m15.5 15.5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
)

// Open a recipe inside its group so comments and group members show; drafts open in the editor
const recipeLink = (recipe) => {
  if (recipe.status === DRAFT_STATUS) {
    return draftRecipeLink(recipe.id, recipe.current_step || 1)
  }

  if (recipe.groups?.length) {
    return groupRecipeLink(recipe.groups[0], recipe)
  }

  return `/${routes.dashboard}/${recipe.slug}-${recipe.id}`
}

const describeWhere = (recipe) => {
  if (recipe.status === DRAFT_STATUS) {
    return 'Your draft'
  }

  if (!recipe.groups?.length) {
    return 'Not shared with a group'
  }

  return recipe.groups.map(({ name }) => name).join(', ')
}

const isTyping = (element) =>
  element &&
  (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) || element.isContentEditable)

const RecipeSearch = ({ className = '' }) => {
  const navigate = useNavigate()
  const wrapRef = useRef(null)
  const inputRef = useRef(null)
  const latestQuery = useRef('')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [failed, setFailed] = useState(false)
  const [highlighted, setHighlighted] = useState(0)

  const trimmed = query.trim()

  // Press "/" anywhere (outside a text box) to start searching
  useEffect(() => {
    const openOnSlash = (e) => {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !isTyping(document.activeElement)) {
        e.preventDefault()
        setOpen(true)
      }
    }
    document.addEventListener('keydown', openOnSlash)
    return () => document.removeEventListener('keydown', openOnSlash)
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }

    inputRef.current?.focus()
    const close = (e) => {
      if (e.type === 'keydown' ? e.key === 'Escape' : !wrapRef.current?.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', close)
    }
  }, [open])

  useEffect(() => {
    latestQuery.current = trimmed
    if (!trimmed) {
      setResults([])
      setSearching(false)
      return
    }

    setSearching(true)
    const timer = setTimeout(() => {
      searchRecipes(trimmed)
        .then(({ data }) => {
          if (latestQuery.current === trimmed) {
            setResults(data || [])
            setHighlighted(0)
            setFailed(false)
          }
        })
        .catch(() => latestQuery.current === trimmed && setFailed(true))
        .finally(() => latestQuery.current === trimmed && setSearching(false))
    }, WAIT_BEFORE_SEARCH_MS)
    return () => clearTimeout(timer)
  }, [trimmed])

  const openRecipe = (recipe) => {
    setOpen(false)
    setQuery('')
    navigate(recipeLink(recipe))
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const step = e.key === 'ArrowDown' ? 1 : -1
      setHighlighted((current) => (current + step + results.length) % (results.length || 1))
    }

    if (e.key === 'Enter' && results[highlighted]) {
      e.preventDefault()
      openRecipe(results[highlighted])
    }
  }

  const status = () => {
    if (!trimmed) {
      return 'Search every recipe you can see, in all your groups, by name, ingredient or cook.'
    }
    if (failed) {
      return 'Search is not working right now. Please try again in a moment.'
    }
    if (searching && !results.length) {
      return 'Searching...'
    }
    if (!results.length) {
      return `No recipes match "${trimmed}".`
    }
    return null
  }

  return (
    <div className={`recipeSearch ${className}`} ref={wrapRef}>
      <button
        type="button"
        className="recipeSearchButton"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label="Search recipes"
        title="Search recipes (press /)"
      >
        <SearchIcon />
      </button>

      {open ? (
        <div className="recipeSearchPanel" role="dialog" aria-label="Search recipes">
          <div className="recipeSearchField">
            <SearchIcon />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search all your recipes"
              aria-label="Search all your recipes"
              maxLength={100}
            />
          </div>

          {status() ? <p className="recipeSearchStatus">{status()}</p> : null}

          {results.length ? (
            <ul className="recipeSearchList">
              {results.map((recipe, index) => (
                <li key={recipe.id}>
                  <button
                    type="button"
                    className={`recipeSearchItem ${
                      index === highlighted ? 'recipeSearchHighlighted' : ''
                    }`}
                    onClick={() => openRecipe(recipe)}
                    onMouseEnter={() => setHighlighted(index)}
                  >
                    <img
                      src={recipe.image || '/images/imageRecipeWithoutPhoto.png'}
                      alt=""
                      className="recipeSearchImage"
                    />
                    <span className="recipeSearchText">
                      <span className="recipeSearchName">{recipe.name || 'Untitled recipe'}</span>
                      <span className="recipeSearchMeta">
                        {creditLine(recipe) ? `${creditLine(recipe)} · ` : ''}
                        {describeWhere(recipe)}
                      </span>
                      {recipe.matched_ingredient ? (
                        <span className="recipeSearchIngredient">
                          Ingredient: {recipe.matched_ingredient}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default RecipeSearch
